import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState, useCallback } from 'react';

interface SlideshowProps {
    images: string[];
    interval?: number; // ms
}

const Slideshow: React.FC<SlideshowProps> = ({ images, interval = 5000 }) => {
    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState(1);
    const [isPaused, setIsPaused] = useState(false);

    const navigateNext = useCallback(() => {
        setDirection(1);
        setIndex((prev) => (prev + 1) % images.length);
    }, [images.length]);

    const navigatePrev = useCallback(() => {
        setDirection(-1);
        setIndex((prev) => (prev - 1 + images.length) % images.length);
    }, [images.length]);

    useEffect(() => {
        if (isPaused) return;
        const timer = setInterval(navigateNext, interval);
        return () => clearInterval(timer);
    }, [isPaused, navigateNext, interval]);

    if (!images || images.length === 0) return null;

    return (
        <Box
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: { xs: '4/3', md: '16/9' },
                backgroundColor: '#070b1c',
                overflow: 'hidden',
                outline: '1px solid #c9a84c',
                outlineOffset: '8px',
                mb: 4,
            }}
        >
            <AnimatePresence initial={false} custom={direction}>
                <motion.img
                    key={index}
                    src={images[index]}
                    alt={`Slide ${index + 1}`}
                    custom={direction}
                    variants={{
                        initial: (dir: number) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
                        animate: { x: 0, opacity: 1 },
                        exit: (dir: number) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 }),
                    }}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                    }}
                />
            </AnimatePresence>

            {/* Navigation Arrows */}
            <IconButton
                onClick={navigatePrev}
                sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'rgba(255,255,255,0.5)',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    zIndex: 2,
                    '&:hover': { color: '#f0ede8', backgroundColor: 'rgba(0,0,0,0.5)' },
                }}
            >
                <ArrowBackIosNewIcon />
            </IconButton>

            <IconButton
                onClick={navigateNext}
                sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'rgba(255,255,255,0.5)',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    zIndex: 2,
                    '&:hover': { color: '#f0ede8', backgroundColor: 'rgba(0,0,0,0.5)' },
                }}
            >
                <ArrowForwardIosIcon />
            </IconButton>

            {/* Progress Dots */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 1.5,
                    zIndex: 2,
                }}
            >
                {images.map((_, i) => (
                    <Box
                        key={i}
                        onClick={() => {
                            setDirection(i > index ? 1 : -1);
                            setIndex(i);
                        }}
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: i === index ? '#c9a84c' : 'rgba(255,255,255,0.3)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': { backgroundColor: '#c9a84c' },
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default Slideshow;
